import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

export interface SystemField {
  key: string;
  name: string;
  description: string;
  example: string;
  dataType: string;
  requiresContext?: string;
}

export interface SystemFieldCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  fields: SystemField[];
}

export interface FieldSearchResult {
  field: SystemField;
  category: { id: string; name: string };
  matchScore: number;
}

export interface ValidationResult {
  valid: boolean;
  systemFields: string[];
  customFields: string[];
  invalidFields: string[];
  errors: string[];
}

export interface PreviewResult {
  content: string;
  resolvedFields: string[];
  unresolvedFields: string[];
}

/**
 * Hook para buscar todos os campos de sistema disponíveis
 */
export function useSystemFields() {
  return useQuery<SystemFieldCategory[]>({
    queryKey: ['system-fields'],
    queryFn: async () => {
      const response = await api.get('/contracts/system-fields');
      return response.data;
    },
    staleTime: 1000 * 60 * 30, // 30 minutos - campos não mudam frequentemente
  });
}


/**
 * Hook para buscar campos de uma categoria específica
 */
export function useSystemFieldsByCategory(categoryId: string) {
  return useQuery<SystemField[]>({
    queryKey: ['system-fields', categoryId],
    queryFn: async () => {
      const response = await api.get(`/contracts/system-fields/${categoryId}`);
      return response.data;
    },
    enabled: !!categoryId,
    staleTime: 1000 * 60 * 30,
  });
}

/**
 * Hook para buscar campos por query
 */
export function useSearchSystemFields(query: string) {
  return useQuery<FieldSearchResult[]>({
    queryKey: ['system-fields', 'search', query],
    queryFn: async () => {
      const response = await api.get(`/contracts/system-fields/search/${encodeURIComponent(query)}`);
      return response.data;
    },
    enabled: query.length >= 2,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook para validar campos em um template
 */
export function useValidateTemplate() {
  return {
    validate: async (content: string): Promise<ValidationResult> => {
      const response = await api.post('/contracts/system-fields/validate', { content });
      return response.data;
    },
  };
}

/**
 * Hook para preview de resolução de campos
 */
export function usePreviewResolution() {
  return {
    preview: async (content: string, context?: Record<string, any>): Promise<PreviewResult> => {
      const response = await api.post('/contracts/system-fields/preview', { content, context });
      return response.data;
    },
  };
}

/**
 * Utilitário para buscar campos localmente (sem API)
 */
export function searchFieldsLocally(
  categories: SystemFieldCategory[],
  query: string
): FieldSearchResult[] {
  if (!query || query.trim().length < 2) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  const results: FieldSearchResult[] = [];

  for (const category of categories) {
    for (const field of category.fields) {
      let matchScore = 0;

      if (field.name.toLowerCase().includes(normalizedQuery)) matchScore += 3;
      if (field.key.toLowerCase().includes(normalizedQuery)) matchScore += 2;
      if (field.description.toLowerCase().includes(normalizedQuery)) matchScore += 1;
      if (category.name.toLowerCase().includes(normalizedQuery)) matchScore += 1;

      if (matchScore > 0) {
        results.push({
          field,
          category: { id: category.id, name: category.name },
          matchScore,
        });
      }
    }
  }

  return results.sort((a, b) => b.matchScore - a.matchScore);
}

/**
 * Utilitário para obter um campo por chave
 */
export function getFieldByKey(
  categories: SystemFieldCategory[],
  key: string
): SystemField | undefined {
  for (const category of categories) {
    const field = category.fields.find(f => f.key === key);
    if (field) return field;
  }
  return undefined;
}

/**
 * Mapa de ícones por categoria
 */
export const CATEGORY_ICONS: Record<string, string> = {
  usuario: 'User',
  conta: 'CreditCard',
  tenant: 'Building',
  contrato: 'FileText',
  asset: 'Coins',
  signatario: 'Users',
  data: 'Calendar',
  entidade_pj: 'Building2',
};

/**
 * Cores por categoria
 */
export const CATEGORY_COLORS: Record<string, string> = {
  usuario: 'blue',
  conta: 'green',
  tenant: 'purple',
  contrato: 'orange',
  asset: 'yellow',
  signatario: 'pink',
  data: 'cyan',
  entidade_pj: 'indigo',
};
